import { Injectable } from '@angular/core';
import { ExcelService } from './excel.service';
import { NgxSpinnerService } from 'ngx-spinner';
import Swal from 'sweetalert2';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';

(pdfMake as any).vfs = (pdfFonts as any);

@Injectable({
  providedIn: 'root'
})
export class DarpanExportService {

  constructor(
    private excelService: ExcelService,
    private spinner: NgxSpinnerService
  ) { }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  async loadHindiFont(): Promise<void> {
    try {
      const regular = await fetch('/assets/fonts/NotoSansDevanagari-Regular.ttf')
        .then(res => res.arrayBuffer());
      const bold = await fetch('/assets/fonts/NotoSansDevanagari-Bold.ttf')
        .then(res => res.arrayBuffer());

      const base64Regular = this.arrayBufferToBase64(regular);
      const base64Bold = this.arrayBufferToBase64(bold);

      if (!(pdfMake as any).vfs) {
        (pdfMake as any).vfs = {};
      }

      (pdfMake as any).vfs['NotoSansDevanagari-Regular.ttf'] = base64Regular;
      (pdfMake as any).vfs['NotoSansDevanagari-Bold.ttf'] = base64Bold;

      (pdfMake as any).fonts = {
        NotoSansDevanagari: {
          normal: 'NotoSansDevanagari-Regular.ttf',
          bold: 'NotoSansDevanagari-Bold.ttf',
          italics: 'NotoSansDevanagari-Regular.ttf',
          bolditalics: 'NotoSansDevanagari-Bold.ttf',
        }
      };
    } catch (e) {
      console.warn("Failed to load Hindi fonts, falling back to default.", e);
    }
  }

  async loadLogo(): Promise<string> {
    try {
      const response = await fetch('/assets/img/acms/acmslogo.png');
      if (!response.ok) throw new Error('Not OK');
      const arrayBuffer = await response.arrayBuffer();
      const base64 = this.arrayBufferToBase64(arrayBuffer);
      return `data:image/png;base64,${base64}`;
    } catch (e) {
      try {
        const response = await fetch('/assets/img/pcs_logo.jpg');
        const arrayBuffer = await response.arrayBuffer();
        const base64 = this.arrayBufferToBase64(arrayBuffer);
        return `data:image/jpeg;base64,${base64}`;
      } catch (err) {
        console.warn("Failed to load logo", err);
        return '';
      }
    }
  }

  exportExcel(data: any[], totals: any, financialYears: string[], fileName: string, sheetTitle: string, isLog: boolean = false) {
    if (!data || data.length === 0) {
      Swal.fire('Info', 'No data available to export.', 'info');
      return;
    }

    const cols = [
      { key: 'state', value: 'State (Code)' },
      { key: 'division', value: 'Division (Code)' },
      { key: 'district', value: 'District (Code)' },
      { key: 'block', value: 'Block (Code)' }
    ];

    financialYears.forEach((year, index) => {
      cols.push({ key: `kpi${index * 2 + 1}`, value: `Sanctioned (${year})` });
      cols.push({ key: `kpi${index * 2 + 2}`, value: `Completed (${year})` });
    });

    const exportData = data.map(item => {
      const row: any = {
        state: `${item.state_name || ''} (${item.state_code ?? ''})`,
        division: `${item.division_name || ''} (${item.division_code ?? ''})`,
        district: `${item.district_name || ''} (${item.district_code ?? ''})`,
        block: isLog
          ? `${item.project_name || ''} (${item.project_code ?? ''})`
          : `${item.block_name || ''} (${item.block_code ?? ''})`
      };
      financialYears.forEach((year, index) => {
        row[`kpi${index * 2 + 1}`] = item[`kpi${index * 2 + 1}_data`] ?? 0;
        row[`kpi${index * 2 + 2}`] = item[`kpi${index * 2 + 2}_data`] ?? 0;
      });
      return row;
    });

    // Append Grand Total row
    const totalRow: any = {
      state: 'Grand Total',
      division: '',
      district: '',
      block: ''
    };
    financialYears.forEach((year, index) => {
      totalRow[`kpi${index * 2 + 1}`] = totals[`kpi${index * 2 + 1}_data`] ?? 0;
      totalRow[`kpi${index * 2 + 2}`] = totals[`kpi${index * 2 + 2}_data`] ?? 0;
    });
    exportData.push(totalRow);

    this.excelService.export(exportData, cols, fileName + '.xlsx', sheetTitle);
  }

  async exportPDF(data: any[], totals: any, financialYears: string[], fileName: string, headerTitle: string, isLog: boolean = false) {
    if (!data || data.length === 0) {
      Swal.fire('Info', 'No data available to export.', 'info');
      return;
    }

    this.spinner.show();
    await this.loadHindiFont();
    const logoDataUrl = await this.loadLogo();

    const headers = ['State (Code)', 'Division (Code)', 'District (Code)', 'Block (Code)'];
    financialYears.forEach(year => {
      headers.push(`Sanctioned (${year})`);
      headers.push(`Completed (${year})`);
    });

    const headerRow = headers.map(h => ({ text: h, style: 'tableHeader' }));

    const body = [
      headerRow,
      ...data.map(item => {
        const row = [
          `${item.state_name || ''} (${item.state_code ?? ''})`,
          `${item.division_name || ''} (${item.division_code ?? ''})`,
          `${item.district_name || ''} (${item.district_code ?? ''})`,
          isLog
            ? `${item.project_name || ''} (${item.project_code ?? ''})`
            : `${item.block_name || ''} (${item.block_code ?? ''})`
        ];
        financialYears.forEach((_, index) => {
          row.push(String(item[`kpi${index * 2 + 1}_data`] ?? 0));
          row.push(String(item[`kpi${index * 2 + 2}_data`] ?? 0));
        });
        return row;
      })
    ];

    // Add total row
    const totalRow = ['Grand Total', '', '', ''];
    financialYears.forEach((_, index) => {
      totalRow.push(String(totals[`kpi${index * 2 + 1}_data`] ?? 0));
      totalRow.push(String(totals[`kpi${index * 2 + 2}_data`] ?? 0));
    });
    body.push(totalRow.map(t => ({ text: t, style: 'tableTotal' }) as any));

    const documentDefinition: any = {
      pageOrientation: 'landscape',
      pageSize: 'B4',
      background: logoDataUrl ? function (currentPage: any, pageSize: any) {
        return {
          image: logoDataUrl,
          width: 300,
          opacity: 0.08,
          absolutePosition: {
            x: (pageSize.width - 300) / 2,
            y: (pageSize.height - 300) / 2
          }
        };
      } : undefined,
      footer: function (currentPage: any, pageCount: any) {
        return [
          {
            columns: [
              { text: `Page ${currentPage} of ${pageCount} | Generated on ${new Date().toLocaleString()}`, alignment: 'right', margin: [0, 10, 20, 0], fontSize: 8 }
            ]
          }
        ];
      },
      content: [
        {
          columns: [
            logoDataUrl ? {
              image: logoDataUrl,
              width: 40,
              alignment: 'left'
            } : { text: '', width: 1 },
            {
              text: headerTitle,
              style: 'header',
              alignment: 'left',
              margin: [10, 10, 0, 0]
            }
          ],
          margin: [0, 0, 0, 20]
        },
        {
          table: {
            headerRows: 1,
            widths: Array(headers.length).fill('*'),
            body: body
          },
          layout: {
            fillColor: (rowIndex: any) => {
              if (rowIndex === 0) return '#728FCE';
              if (rowIndex === body.length - 1) return '#eff6ff';
              return rowIndex % 2 === 0 ? '#f8fafc' : null;
            }
          },
          dontBreakRows: true,
          keepWithHeaderRows: 1
        }
      ],
      defaultStyle: {
        font: (pdfMake as any).fonts?.NotoSansDevanagari ? 'NotoSansDevanagari' : 'Roboto',
        fontSize: 9,
        lineHeight: 1.2
      },
      styles: {
        header: { fontSize: 16, bold: true, color: '#1e293b' },
        tableHeader: {
          bold: true,
          fontSize: 10,
          color: 'white',
          alignment: 'center'
        },
        tableTotal: {
          bold: true,
          fontSize: 9,
          color: 'black'
        }
      }
    };

    pdfMake.createPdf(documentDefinition).download(fileName + '.pdf');
    this.spinner.hide();
  }
}
